// OPERATOR-CONSOLE: the Settings sections beyond a plain setting card — Prices (the platform
// price list, margin by model), Baseline (the rendered baseline, Apply baseline), Roster (per-DE
// overrides), Base prompt (the rules text, a version diff) and Satellites (one satellite's
// overrides).
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  FieldError,
  NativeSelect,
  NativeSelectOption,
  Notice,
  QuickAddDialog,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Building2Icon,
  PencilIcon,
  RocketIcon,
  ServerIcon,
  TrendingUpIcon,
  UsersIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../../api/client";
import { dollars } from "../../billing/billing-money";
import { monthName, recentUtcMonths, utcMonth } from "../../billing/billing-queries";
import { formatDateTime } from "../../format";
import { problemText } from "../../ops-parts";
import { LinkButton } from "../../ops-ux";
import { LoadError, Loading } from "../../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";
import { PriceList } from "../billing/price-list";
import { CheckField, KvList, SelectField, TextField } from "./fields";
import { type Job, JobStateBadge, JobSteps, jobsKey } from "./operator-agents";
import { SettingCard, scopeOf, settingQuery, useSettingsScope } from "./setting-card";

// Prices ---------------------------------------------------------------------------------------------

export function PricesSection({ api }: { api: Api }) {
  return (
    <div className="flex min-w-0 flex-col gap-6" data-slot="settings-prices">
      <PriceList api={api} />
      <MarginByModel api={api} />
      <div className="flex">
        <LinkButton size="xs" render={<Link to="/operator/billing" search={{ tab: "plans" }} />}>
          Plans and subscriptions in Billing
        </LinkButton>
      </div>
    </div>
  );
}

function MarginByModel({ api }: { api: Api }) {
  const months = useMemo(() => recentUtcMonths(new Date()), []);
  const [month, setMonth] = useState(() => utcMonth(new Date()));
  const scope = useSettingsScope();
  const [company, setCompany] = useState("");
  const q = useQuery({
    queryKey: ["operator", "margin-models", month, company],
    queryFn: () =>
      api.client.operator.margin.models({
        query: { month, ...(company ? { company_id: company } : {}) },
      }),
  });
  return (
    <section className="flex min-w-0 flex-col gap-3" data-slot="margin-by-model">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Margin by model</h2>
        <div className="flex flex-wrap gap-2">
          <NativeSelect
            aria-label="Company"
            size="sm"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          >
            <NativeSelectOption value="">All companies</NativeSelectOption>
            {scope.companies.map((c) => (
              <NativeSelectOption key={c.id} value={c.id}>
                {c.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Month"
            size="sm"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => (
              <NativeSelectOption key={m} value={m}>
                {monthName(m)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
      </div>
      {q.isPending ? (
        <Loading label="Loading margin" rows={2} />
      ) : q.isError ? (
        <LoadError what="Margin" error={q.error} />
      ) : q.data.rows.length === 0 ? (
        <EmptyState variant="card" icon={TrendingUpIcon} title="No runs this month" />
      ) : (
        <Table className={DENSE_TABLE} data-slot="margin-models-table">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Company</TableHead>
              <TableHead scope="col">Model</TableHead>
              <TableHead scope="col" className="text-right">
                Runs
              </TableHead>
              <TableHead scope="col" className="text-right">
                Cost
              </TableHead>
              <TableHead scope="col" className="text-right">
                Price
              </TableHead>
              <TableHead scope="col" className="text-right">
                Margin
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.rows.map((r) => (
              <TableRow key={`${r.company_id}:${r.model ?? ""}`} data-margin-model={r.model ?? ""}>
                <TableHead scope="row" className="font-normal">
                  {r.company_name}
                </TableHead>
                <TableCell className="break-all">{r.model ?? "Unknown"}</TableCell>
                <TableCell className="text-right tabular-nums">{r.runs}</TableCell>
                <TableCell className="text-right tabular-nums">{dollars(r.cost_micros)}</TableCell>
                <TableCell className="text-right tabular-nums">{dollars(r.price_micros)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {dollars(r.margin_micros)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableHead scope="row" colSpan={2} className="font-medium">
                Total
              </TableHead>
              <TableCell className="text-right tabular-nums">{q.data.totals.runs}</TableCell>
              <TableCell className="text-right tabular-nums">
                {dollars(q.data.totals.cost_micros)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {dollars(q.data.totals.price_micros)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {dollars(q.data.totals.margin_micros)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </section>
  );
}

// Baseline -------------------------------------------------------------------------------------------

type Baseline = OperationOutput<"operator.baseline.get">;
const baselineQuery = (api: Api, company: string | null, satellite: string | null) =>
  queryOptions({
    queryKey: ["operator", "baseline", company ?? "", satellite ?? ""],
    queryFn: () =>
      api.client.operator.baseline.get({
        query: {
          ...(company ? { company_id: company } : {}),
          ...(satellite ? { satellite_id: satellite } : {}),
        },
      }),
  });

export function BaselineSection({ api }: { api: Api }) {
  const s = useSettingsScope();
  const at = scopeOf("baseline", s);
  const [applying, setApplying] = useState(false);
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="settings-baseline">
      <SettingCard
        api={api}
        settingKey="baseline"
        actions={
          at.scope === "satellite" && at.satellite ? (
            <Button size="xs" onClick={() => setApplying(true)}>
              <RocketIcon aria-hidden /> Apply baseline
            </Button>
          ) : null
        }
      />
      <RenderedBaseline api={api} company={at.company} satellite={at.satellite} />
      {applying && at.satellite ? (
        <ApplyBaselineDialog
          api={api}
          satellite={at.satellite}
          onClose={() => setApplying(false)}
        />
      ) : null}
    </div>
  );
}

function RenderedBaseline({
  api,
  company,
  satellite,
}: {
  api: Api;
  company: string | null;
  satellite: string | null;
}) {
  const q = useQuery(baselineQuery(api, company, satellite));
  return (
    <section
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4"
      data-slot="rendered-baseline"
      aria-label="Rendered baseline"
    >
      <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        Rendered baseline
        {q.data ? (
          <Badge variant="neutral" data-slot="baseline-hash">
            <code>{q.data.baseline.version.slice(0, 12)}</code>
          </Badge>
        ) : null}
      </h2>
      {q.isPending ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <Notice tone="danger">{problemText(q.error, "The baseline could not be rendered.")}</Notice>
      ) : (
        <BaselineFacts view={q.data} />
      )}
    </section>
  );
}

function BaselineFacts({ view }: { view: Baseline }) {
  const b = view.baseline;
  const sources = Object.entries(b.sources);
  return (
    <>
      <KvList
        rows={[
          {
            key: "version",
            label: "Baseline",
            value: b.baseline_version ?? "Shipped with ClawPanel",
          },
          {
            key: "profile",
            label: "Profile",
            value: `${b.profile} · ${b.runtime.max_sessions} sessions`,
          },
          {
            key: "containers",
            label: "Containers",
            value:
              b.containers.length === 0
                ? "All alike"
                : b.containers.map((c) => `${c.container} (${c.profile})`).join(", "),
          },
          { key: "models", label: "Role kinds", value: String(b.models.kinds.length) },
          { key: "prompt", label: "Base prompt", value: b.base_prompt_version ?? "Shipped" },
          { key: "skills", label: "Packs", value: String(b.skills.length) },
          {
            key: "sources",
            label: "From",
            value:
              sources.length === 0
                ? "Defaults only"
                : sources.map(([k, v]) => `${k} v${v}`).join(", "),
          },
          { key: "at", label: "Rendered", value: formatDateTime(view.generated_at) },
        ]}
      />
      <details className="min-w-0" data-slot="baseline-json">
        <summary className="cursor-pointer text-xs text-muted-foreground">JSON</summary>
        <pre className="mt-2 max-h-80 overflow-y-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-[11px]">
          {JSON.stringify(b, null, 2)}
        </pre>
      </details>
    </>
  );
}

function ApplyBaselineDialog({
  api,
  satellite,
  onClose,
}: {
  api: Api;
  satellite: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [container, setContainer] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [job, setJob] = useState<Job | null>(null);
  const apply = useMutation({
    mutationFn: () =>
      api.client.operator.deployer.applyBaseline({
        body: { satellite_id: satellite, container: container?.trim() || null, dry_run: dryRun },
      }),
    onSuccess: (j) => {
      setJob(j);
      void qc.invalidateQueries({ queryKey: jobsKey });
    },
  });
  if (job)
    return (
      <QuickAddDialog
        title="Apply baseline"
        submitLabel="Done"
        onSubmit={onClose}
        onClose={onClose}
        slot="apply-baseline-job"
      >
        <div className="flex items-center gap-2 text-sm">
          Job <JobStateBadge state={job.state} />
        </div>
        <JobSteps job={job} />
      </QuickAddDialog>
    );
  return (
    <QuickAddDialog
      title="Apply baseline"
      info="The Deployer renders the baseline for this satellite and applies it. A dry run only reports what would change."
      submitLabel={dryRun ? "Dry run" : "Apply"}
      pending={apply.isPending}
      onSubmit={() => apply.mutate()}
      onClose={onClose}
      error={
        apply.error ? <FieldError>{problemText(apply.error, "Not started.")}</FieldError> : null
      }
      slot="apply-baseline-dialog"
    >
      <TextField
        label="Container"
        nullable
        placeholder="All containers"
        value={container}
        onChange={setContainer}
      />
      <CheckField label="Dry run" checked={dryRun} onChange={setDryRun} />
    </QuickAddDialog>
  );
}

// Roster ----------------------------------------------------------------------------------------------

type RosterDe = OperationOutput<"operator.roster.get">["des"][number];
type OverrideRow = {
  membership_id: string;
  satellite_id: string | null;
  container: string | null;
  profile: string | null;
  model: string | null;
  runtime_profile: "light" | "standard" | "heavy" | null;
};

/** A company select writing the page's company. */
function CompanySelect() {
  const s = useSettingsScope();
  return (
    <NativeSelect
      aria-label="Company"
      size="sm"
      value={s.company ?? ""}
      onChange={(e) => s.setScope({ company: e.target.value || null, satellite: null })}
    >
      <NativeSelectOption value="">Pick a company</NativeSelectOption>
      {s.companies.map((c) => (
        <NativeSelectOption key={c.id} value={c.id}>
          {c.name}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}

export function RosterSection({ api }: { api: Api }) {
  const s = useSettingsScope();
  const company = s.company;
  const q = useQuery({
    queryKey: ["operator", "roster", company ?? ""],
    queryFn: () => api.client.operator.roster.get({ query: { company_id: company as string } }),
    enabled: Boolean(company),
  });
  const [editing, setEditing] = useState<RosterDe | null>(null);
  return (
    <section className="flex min-w-0 flex-col gap-3" data-slot="settings-roster">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          Roster
          {q.data?.overrides_version ? (
            <Badge variant="accent">Overrides v{q.data.overrides_version}</Badge>
          ) : null}
        </h2>
        <CompanySelect />
      </div>
      {!company ? (
        <EmptyState variant="card" icon={Building2Icon} title="Pick a company" />
      ) : q.isPending ? (
        <Loading label="Loading the roster" rows={2} />
      ) : q.isError ? (
        <LoadError what="The roster" error={q.error} />
      ) : q.data.des.length === 0 ? (
        <EmptyState variant="card" icon={UsersIcon} title="No DEs placed on a satellite" />
      ) : (
        <Table className={DENSE_TABLE} data-slot="roster-table">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">DE</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Satellite
              </TableHead>
              <TableHead scope="col" className="max-lg:hidden">
                Container
              </TableHead>
              <TableHead scope="col" className="max-lg:hidden">
                Profile
              </TableHead>
              <TableHead scope="col">Model</TableHead>
              <TableHead scope="col">Runtime</TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.des.map((d) => (
              <TableRow key={d.membership_id} data-de={d.name} className="group/row">
                <TableHead scope="row" className="font-normal">
                  <span className="flex flex-wrap items-center gap-1">
                    {d.name}
                    {d.overridden.length > 0 ? (
                      <Badge variant="info" data-slot="roster-override">
                        Override
                      </Badge>
                    ) : null}
                  </span>
                </TableHead>
                <TableCell className="max-md:hidden">
                  {d.satellite_name ?? "—"}
                  {d.overridden.includes("placement") ? " *" : ""}
                </TableCell>
                <TableCell className="max-lg:hidden">{d.container ?? "—"}</TableCell>
                <TableCell className="max-lg:hidden">{d.profile ?? "—"}</TableCell>
                <TableCell className="break-all">
                  {d.model ?? "Default"}
                  {d.overridden.includes("model") ? " *" : ""}
                </TableCell>
                <TableCell>
                  {d.runtime_profile}
                  {d.overridden.includes("runtime_profile") ? " *" : ""}
                </TableCell>
                <TableCell className="w-px text-right">
                  <RowActions>
                    <RowAction
                      icon={PencilIcon}
                      label={`Edit ${d.name}`}
                      tip="Edit override"
                      onClick={() => setEditing(d)}
                    />
                  </RowActions>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing && company ? (
        <RosterDialog api={api} company={company} de={editing} onClose={() => setEditing(null)} />
      ) : null}
    </section>
  );
}

function RosterDialog({
  api,
  company,
  de,
  onClose,
}: {
  api: Api;
  company: string;
  de: RosterDe;
  onClose: () => void;
}) {
  const current = useQuery(settingQuery(api, "roster_overrides", company, null));
  if (current.isPending) return null;
  const list = ((current.data?.override ?? { des: [] }) as { des: OverrideRow[] }).des;
  return <RosterForm api={api} company={company} de={de} list={list} onClose={onClose} />;
}

function RosterForm({
  api,
  company,
  de,
  list,
  onClose,
}: {
  api: Api;
  company: string;
  de: RosterDe;
  list: OverrideRow[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const s = useSettingsScope();
  const mine = list.find((r) => r.membership_id === de.membership_id);
  const [row, setRow] = useState<OverrideRow>(
    () =>
      mine ?? {
        membership_id: de.membership_id,
        satellite_id: null,
        container: null,
        profile: null,
        model: null,
        runtime_profile: null,
      },
  );
  const [note, setNote] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const empty =
        !row.satellite_id && !row.container && !row.profile && !row.model && !row.runtime_profile;
      const others = list.filter((r) => r.membership_id !== de.membership_id);
      return api.client.operator.settings.put({
        params: { key: "roster_overrides" },
        body: {
          company_id: company,
          value: { des: empty ? others : [...others, row] },
          ...(note ? { note } : {}),
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["operator", "roster"] });
      void qc.invalidateQueries({ queryKey: ["operator", "settings"] });
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title={`${de.name} override`}
      info="Only what differs from the generated roster. Empty fields follow the roster."
      submitLabel="Save version"
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      error={save.error ? <FieldError>{problemText(save.error, "Not saved.")}</FieldError> : null}
      slot="roster-dialog"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          label="Satellite"
          value={row.satellite_id ?? ""}
          options={[
            { value: "", label: "As placed" },
            ...s.satellites.map((x) => ({ value: x.id, label: x.name })),
          ]}
          onChange={(x) => setRow({ ...row, satellite_id: x || null })}
        />
        <TextField
          label="Container"
          nullable
          placeholder="As placed"
          value={row.container}
          onChange={(x) => setRow({ ...row, container: x })}
        />
        <TextField
          label="Profile"
          nullable
          placeholder="As placed"
          value={row.profile}
          onChange={(x) => setRow({ ...row, profile: x })}
        />
        <TextField
          label="Model"
          nullable
          placeholder="Default"
          value={row.model}
          onChange={(x) => setRow({ ...row, model: x })}
        />
        <SelectField
          label="Runtime profile"
          value={row.runtime_profile ?? ""}
          options={[
            { value: "", label: "Baseline's" },
            { value: "light", label: "Light" },
            { value: "standard", label: "Standard" },
            { value: "heavy", label: "Heavy" },
          ]}
          onChange={(x) =>
            setRow({ ...row, runtime_profile: (x || null) as OverrideRow["runtime_profile"] })
          }
        />
        <TextField label="Note" nullable placeholder="Why" value={note} onChange={setNote} />
      </div>
    </QuickAddDialog>
  );
}

// Base prompt ---------------------------------------------------------------------------------------

export function BasePromptSection({ api }: { api: Api }) {
  const rules = useQuery({
    queryKey: ["operator", "base-rules"],
    queryFn: () => api.client.operator.baseRules.get(),
  });
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="settings-base-prompt">
      <SettingCard api={api} settingKey="base_prompt" />
      <VersionDiff api={api} />
      <section
        className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4"
        data-slot="base-rules"
        aria-label="Base rules"
      >
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          Base rules
          {rules.data ? <Badge variant="neutral">{rules.data.version}</Badge> : null}
        </h2>
        {rules.isPending ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : rules.isError ? (
          <Notice tone="danger">
            {problemText(rules.error, "The rules could not be loaded.")}
          </Notice>
        ) : (
          <ol className="grid gap-3">
            {rules.data.rules.map((r) => (
              <li key={r.key} className="grid gap-1" data-rule={r.key}>
                <span className="flex items-center gap-2 text-xs font-medium">
                  {r.title}
                  <Badge variant="outline">{r.applies_to === "all" ? "DEs and AGs" : "DEs"}</Badge>
                </span>
                <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">
                  {r.text}
                </p>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

/** Two versions of the pin side by side: the lines that differ. */
function VersionDiff({ api }: { api: Api }) {
  const s = useSettingsScope();
  const at = scopeOf("base_prompt", s);
  const q = useQuery(settingQuery(api, "base_prompt", at.company, null));
  const history = q.data?.history ?? [];
  const [a, setA] = useState<number | null>(null);
  const [b, setB] = useState<number | null>(null);
  if (history.length < 2) return null;
  const from = history.find((h) => h.version === (a ?? history[1]?.version));
  const to = history.find((h) => h.version === (b ?? history[0]?.version));
  const lines = (v: unknown) =>
    v === null ? ["(cleared)"] : JSON.stringify(v, null, 2).split("\n");
  const left = lines(from?.value ?? null);
  const right = lines(to?.value ?? null);
  const pick = (value: number | undefined, set: (n: number) => void, label: string) => (
    <NativeSelect
      aria-label={label}
      size="sm"
      value={String(value ?? "")}
      onChange={(e) => set(Number(e.target.value))}
    >
      {history.map((h) => (
        <NativeSelectOption key={h.version} value={String(h.version)}>
          v{h.version}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
  return (
    <section
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-card p-4"
      data-slot="version-diff"
      aria-label="Compare versions"
    >
      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        Compare
        {pick(from?.version, setA, "From version")}
        <span className="text-muted-foreground">→</span>
        {pick(to?.version, setB, "To version")}
      </div>
      <pre className="whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-[11px]">
        {left
          .filter((l) => !right.includes(l))
          .map((l) => `- ${l}`)
          .concat(right.filter((l) => !left.includes(l)).map((l) => `+ ${l}`))
          .join("\n") || "No difference"}
      </pre>
    </section>
  );
}

// Satellites -------------------------------------------------------------------------------------------

export function SatellitesSection({ api }: { api: Api }) {
  const s = useSettingsScope();
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="settings-satellites">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <NativeSelect
          aria-label="Satellite"
          size="sm"
          value={s.satellite ?? ""}
          onChange={(e) => s.setScope({ company: null, satellite: e.target.value || null })}
        >
          <NativeSelectOption value="">Pick a satellite</NativeSelectOption>
          {s.satellites.map((x) => (
            <NativeSelectOption key={x.id} value={x.id}>
              {x.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <LinkButton size="xs" render={<Link to="/operator/satellites" />}>
          <ServerIcon aria-hidden /> All satellites
        </LinkButton>
      </div>
      {s.satellites.length === 0 ? (
        <EmptyState variant="card" icon={ServerIcon} title="No satellites registered" />
      ) : !s.satellite ? (
        <EmptyState variant="card" icon={ServerIcon} title="Pick a satellite" />
      ) : (
        <>
          <SettingCard api={api} settingKey="bridge_runtime" lock="satellite" />
          <SettingCard api={api} settingKey="runtime_profiles" lock="satellite" />
          <SettingCard api={api} settingKey="baseline" lock="satellite" />
        </>
      )}
    </div>
  );
}
